import React, { ReactElement } from 'react';
import './SprigFeedback.scss';
import classNames from 'classnames';
import { isEventKeyDown } from '../../helpers/utility';

export type SprigFeedbackProps = {
  /**
   * The name of the survey to be launched when the user clicks the component.
   * This can be found in Sprig.
   */
  surveyName?: string;
  /**
   * The message to be displayed in the component.
   * Default is "New Feature: Provide Feedback".
   */
  message?: string | ReactElement;
  /**
   * A callback that can be invoked after the user clicks the component and the survey is launched (if a Survey Name was provided).
   */
  onFeedbackClick?: () => void | Promise<void>;
  /**
   * A boolean that indicates whether to hide the icon in the component.
   * Default is false.
   */
  hideIcon?: boolean;
  /**
   * A string that contains additional class names to be applied to the component.
   * This is useful for custom styling
   */
  classNames?: string;
  /**
   * A string that contains the name of the feature to be displayed in the component.
   * If set this will replace the text 'Provide Feedback' in the message.
   */
  featureName?: string;
  /**
   * A boolean that indicates whether to automatically load the survey on component mount.
   * Default is false.
   */
  autoLoad?: boolean;
};

export default function SprigFeedback(props: SprigFeedbackProps) {
  const sprig = window['Sprig'];

  //in production hide sprig if its not loaded.
  //its never loaded in other envs so show it to be able to test ui
  if (process.env.NODE_ENV === 'production' && sprig == null) {
    return <></>;
  }

  const handleProvideFeedbackClick = () => {
    !sprig && alert(`Sprig Survey "${props.surveyName}" Cannot Launch In Test Environments`);
    props.surveyName && sprig && sprig('track', props.surveyName);
    props.onFeedbackClick && props.onFeedbackClick();
  };

  // If autoLoad is true, trigger the feedback and don't render the visual component
  if (props.autoLoad) {
    handleProvideFeedbackClick();
    return <></>;
  }

  const handleKeyDown = (event) => {
    if (!isEventKeyDown(event)) {
      return;
    }
    handleProvideFeedbackClick();
  };

  return (
    <div className={classNames('sprig-feedback', props.classNames)} onClick={handleProvideFeedbackClick} tabIndex={0} onKeyDown={handleKeyDown}>
      {!props.hideIcon && (
        <div className="left-icon-container">
          <i className="left-icon" />
        </div>
      )}
      {props.message ? (
        props.message
      ) : (
        <div className="display-text">
          <span>New Feature: </span>
          <span className="highlight">{props.featureName || 'Provide Feedback'}</span>
        </div>
      )}
    </div>
  );
}
